/** @jsxImportSource @emotion/react */
import React from 'react';
import { Select, Typography, Button, Space } from 'antd';
import { PlusOutlined, SettingOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { css } from '@emotion/react';
import { useListSavesQuery } from '../../api/savesV2Api';
import type { Save } from '../../types/saves';

const styles = {
  saveName: css({ color: '#001529' }),
  selector: css({ minWidth: 200 }),
};

const { Text } = Typography;

export default function SaveSelector() {
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useSelector((state: any) => Boolean(state.auth.accessToken));
  const { data: saves = [], isLoading } = useListSavesQuery(
    { status: 'active', limit: 100 },
    { skip: !isAuthenticated },
  );

  const saveIdFromUrl = React.useMemo(() => {
    const match = location.pathname.match(/^\/saves\/([^/]+)/);
    return match ? match[1] : null;
  }, [location.pathname]);

  const selectedSaveExists = React.useMemo(() => (
    Boolean(saveIdFromUrl && saves.some((save) => save.id === saveIdFromUrl))
  ), [saveIdFromUrl, saves]);

  const handleSaveChange = (value: string) => {
    navigate(`/saves/${value}`);
  };

  if (!saves || saves.length === 0) {
    return (
      <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={() => navigate('/saves')}
      >
        Создать сейв
      </Button>
    );
  }

  if (saves.length === 1) {
    const save = saves[0];
    return (
      <Space>
        <Text strong css={styles.saveName}>
          {save.farmName || save.id.slice(0, 8)}
        </Text>
        <Button
          type="text"
          size="small"
          icon={<SettingOutlined />}
          onClick={() => navigate('/saves')}
        />
      </Space>
    );
  }

  const options = saves.map((save: Save) => ({
    label: save.farmName ? `${save.farmName} · ${save.type}` : `${save.type} ${save.id.slice(0, 8)}...`,
    value: save.id,
  }));

  return (
    <Space>
      <Select
        value={selectedSaveExists ? saveIdFromUrl : undefined}
        onChange={handleSaveChange}
        loading={isLoading}
        css={styles.selector}
        options={options}
        placeholder="Выберите сейв"
      />
      <Button
        type="text"
        icon={<SettingOutlined />}
        onClick={() => navigate('/saves')}
        title="Сейвы"
      />
    </Space>
  );
}